import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { DynamicThemeProvider } from "@/components/theme/DynamicThemeProvider";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { CookieConsentManager } from "@/components/cookie-consent/CookieConsentManager";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ReferralPage from "./pages/Referral";
import ReferralRedirect from "./pages/ReferralRedirect";
import ResetPassword from "./pages/ResetPassword";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import CustomerDashboard from "./pages/customer/Dashboard";
import CustomerRewards from "./pages/customer/Rewards";
import CustomerVisitRewards from "./pages/customer/VisitRewards";
import CustomerHistory from "./pages/customer/History";
import CustomerProfile from "./pages/customer/Profile";
import CustomerSettings from "./pages/customer/Settings";
import ReferralStats from "./pages/customer/ReferralStats";
import Leaderboard from "./pages/customer/Leaderboard";
import CustomerSocialEngagement from "./pages/customer/SocialEngagement";
import CustomerCompetitions from "./pages/customer/Competitions";
import SuperadminDashboard from "./pages/superadmin/Dashboard";
import TenantManagement from "./pages/superadmin/TenantManagement";
import UserManagement from "./pages/superadmin/UserManagement";
import SuperadminSettings from "./pages/superadmin/Settings";
import SuperadminSocialEngagement from "./pages/superadmin/SocialEngagement";
import SystemCRM from "./pages/superadmin/SystemCRM";
import SystemLandingPages from "./pages/superadmin/SystemLandingPages";
import AdminDashboard from "./pages/admin/Dashboard";
import CustomerManagement from "./pages/admin/CustomerManagement";
import CustomerDetail from "./pages/admin/CustomerDetail";
import QRCodeManager from "./pages/admin/QRCodeManager";
import StampManagement from "./pages/admin/StampManagement";
import AdminSettings from "./pages/admin/Settings";
import AdminSocialEngagement from "./pages/admin/SocialEngagement";
import RewardsManager from "./pages/admin/RewardsManager";
import VisitRewards from "./pages/admin/VisitRewards";
import AdminCompetitions from "./pages/admin/Competitions";
import RewardTemplatesManager from "./pages/superadmin/RewardTemplatesManager";
import VisitRewardTemplates from "./pages/superadmin/VisitRewardTemplates";
import Analytics from "./pages/admin/Analytics";
import ReferralCRM from "./pages/admin/ReferralCRM";
import LandingPageBuilder from "./pages/admin/LandingPageBuilder";
import FullscreenBuilder from "./pages/admin/FullscreenBuilder";
import PublicLandingPage from "./pages/PublicLandingPage";
import InvitationLandingPage from "./pages/InvitationLandingPage";
import SuperadminFullscreenBuilder from "./pages/superadmin/FullscreenBuilder";
import SuperadminTemplateBuilder from "./pages/superadmin/TemplateBuilder";
import ProtectedRoute from "./components/layout/ProtectedRoute";
import SessionWarning from "./components/security/SessionWarning";
import AuthRedirect from "./components/auth/AuthRedirect";
import MaintenancePage from "./components/maintenance/MaintenancePage";
import { useMaintenanceMode } from "./hooks/useMaintenanceMode";
import { useAuth } from "./hooks/useAuth";

const queryClient = new QueryClient();

const App = () => {
  const { isMaintenanceMode, loading: maintenanceLoading } = useMaintenanceMode()
  const { userRole } = useAuth()
  
  // Show maintenance page if maintenance mode is enabled and user is not superadmin
  if (!maintenanceLoading && isMaintenanceMode && userRole !== 'superadmin') {
    return <MaintenancePage />
  }

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CookieConsentProvider>
        <DynamicThemeProvider>
          <Toaster />
          <Sonner />
          <SessionWarning />
          <BrowserRouter>
          <CookieConsentManager />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/anmelden" element={<Login />} />
            <Route path="/registrieren" element={<Register />} />
            <Route path="/einladung/:inviteToken" element={<InvitationLandingPage />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/ref/:code" element={<ReferralPage />} />
            <Route path="/ref/:code/:landingPage" element={<ReferralPage />} />
            <Route path="/track/:code" element={<ReferralRedirect />} />
            <Route path="/track/:code/:landingPage" element={<ReferralRedirect />} />
            
            {/* Public Landing Pages */}
            <Route path="/lp/:tenantSlug/:pageSlug" element={<PublicLandingPage />} />
            
            {/* Auth Redirect Route */}
            <Route path="/auth/redirect" element={<AuthRedirect />} />
            
            {/* Superadmin Routes */}
            <Route path="/superadmin" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperadminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/unternehmen" element={
              <ProtectedRoute requiredRole="superadmin">
                <TenantManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/benutzer" element={
              <ProtectedRoute requiredRole="superadmin">
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/crm" element={
              <ProtectedRoute requiredRole="superadmin">
                <SystemCRM />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/landing-pages" element={
              <ProtectedRoute requiredRole="superadmin">
                <SystemLandingPages />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/builder/:pageId" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperadminFullscreenBuilder />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/template-builder/:templateId" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperadminTemplateBuilder />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/einstellungen" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperadminSettings />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/soziales-engagement" element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperadminSocialEngagement />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/unterstuetzer" element={
              <ProtectedRoute requiredRole="admin">
                <CustomerManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/unterstuetzer/:customerId" element={
              <ProtectedRoute requiredRole="admin">
                <CustomerDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/qr-codes" element={
              <ProtectedRoute requiredRole="admin">
                <QRCodeManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/stempel" element={
              <ProtectedRoute requiredRole="admin">
                <StampManagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/belohnungen" element={
              <ProtectedRoute requiredRole="admin">
                <RewardsManager />
              </ProtectedRoute>
            } />
            <Route path="/admin/besucherprämien" element={
              <ProtectedRoute requiredRole="admin">
                <VisitRewards />
              </ProtectedRoute>
            } />
            <Route path="/admin/referral-crm" element={
              <ProtectedRoute requiredRole="admin">
                <ReferralCRM />
              </ProtectedRoute>
            } />
            <Route path="/admin/landing-pages" element={
              <ProtectedRoute requiredRole="admin">
                <LandingPageBuilder />
              </ProtectedRoute>
            } />
            <Route path="/admin/builder/:pageId" element={
              <ProtectedRoute requiredRole="admin">
                <FullscreenBuilder />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytik" element={
              <ProtectedRoute requiredRole="admin">
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="/admin/einstellungen" element={
              <ProtectedRoute requiredRole="admin">
                <AdminSettings />
              </ProtectedRoute>
            } />
            <Route path="/admin/soziales-engagement" element={
              <ProtectedRoute requiredRole="admin">
                <AdminSocialEngagement />
              </ProtectedRoute>
            } />
            <Route path="/admin/wettbewerbe" element={
              <ProtectedRoute requiredRole="admin">
                <AdminCompetitions />
              </ProtectedRoute>
            } />
            
            {/* Customer Routes */}
            <Route path="/kunde" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/kunde/praemien" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerRewards />
              </ProtectedRoute>
            } />
            <Route path="/kunde/besucherprämien" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerVisitRewards />
              </ProtectedRoute>
            } />
            <Route path="/kunde/verlauf" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerHistory />
              </ProtectedRoute>
            } />
            <Route path="/kunde/profil" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerProfile />
              </ProtectedRoute>
            } />
            <Route path="/kunde/empfehlungen" element={
              <ProtectedRoute requiredRole="customer">
                <ReferralStats />
              </ProtectedRoute>
            } />
            <Route path="/kunde/bestenliste" element={
              <ProtectedRoute requiredRole="customer">
                <Leaderboard />
              </ProtectedRoute>
            } />
            <Route path="/kunde/soziales-engagement" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerSocialEngagement />
              </ProtectedRoute>
            } />
            <Route path="/kunde/wettbewerbe" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerCompetitions />
              </ProtectedRoute>
            } />
            <Route path="/kunde/einstellungen" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerSettings />
              </ProtectedRoute>
            } />

            {/* Customer Routes - English aliases for compatibility */}
            <Route path="/customer" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/customer/rewards" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerRewards />
              </ProtectedRoute>
            } />
            <Route path="/customer/history" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerHistory />
              </ProtectedRoute>
            } />
            <Route path="/customer/profile" element={
              <ProtectedRoute requiredRole="customer">
                <CustomerProfile />
              </ProtectedRoute>
            } />
            <Route path="/customer/referrals" element={
              <ProtectedRoute requiredRole="customer">
                <ReferralStats />
              </ProtectedRoute>
            } />
            <Route path="/customer/leaderboard" element={
              <ProtectedRoute requiredRole="customer">
                <Leaderboard />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/prämien-vorlagen" element={
              <ProtectedRoute requiredRole="superadmin">
                <RewardTemplatesManager />
              </ProtectedRoute>
            } />
            <Route path="/superadmin/besucherprämien-vorlagen" element={
              <ProtectedRoute requiredRole="superadmin">
                <VisitRewardTemplates />
              </ProtectedRoute>
            } />
            
            {/* Legal Pages */}
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </BrowserRouter>
        </DynamicThemeProvider>
      </CookieConsentProvider>
    </TooltipProvider>
  </QueryClientProvider>
  )
}

export default App;
